import Reveal from "./Reveal";
import { Skeleton } from "./Skeleton";

// SVG half-circle gauge — 180° sweep, dasharray fill, needle at value.
//
// While `loading`, the dial is real and reads zero: full track, empty arc,
// needle parked at the left stop. Only the numeric reading is a placeholder.
// The arc and needle stay mounted across the swap, so `anim-arc`/`anim-needle`
// sweep them up to the real value once it lands.
export default function GaugeChart({
  value,
  max,
  label,
  unit = "",
  color = "var(--accent)",
  size = 140,
  loading = false,
}: {
  value: number;
  max: number;
  label: string;
  unit?: string;
  color?: string;
  size?: number;
  loading?: boolean;
}) {
  const sw = 12;
  const pad = sw / 2 + 2;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - pad;
  const frac = loading ? 0 : Math.min(Math.max(value / max, 0), 1);

  // Semicircle left → right over the top; dasharray trims it to the value.
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const len = Math.PI * r;

  const nr = r - sw / 2 - 4; // needle length

  return (
    <Reveal>
    <div className="flex flex-col items-center">
      <svg viewBox={`0 0 ${size} ${cy + pad}`} width={size} height={cy + pad}>
        <path d={arc} fill="none" stroke="var(--neu-dark)" strokeWidth={sw} strokeLinecap="round" opacity={0.25} />
        <path
          className="anim-arc"
          d={arc}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={`${frac * len} ${len}`}
        />
        {/* Needle drawn pointing left (value 0), swung clockwise by rotation. */}
        <g
          className="anim-needle"
          style={{
            transformBox: "view-box",
            transformOrigin: `${cx}px ${cy}px`,
            transform: `rotate(${frac * 180}deg)`,
          }}
        >
          <line x1={cx} y1={cy} x2={cx - nr} y2={cy} stroke="var(--text)" strokeWidth={2} strokeLinecap="round" />
        </g>
        <circle cx={cx} cy={cy} r={3.5} fill="var(--text)" />
      </svg>
      {loading ? (
        // Matches the reading's box so the dial does not shift when it lands.
        <Skeleton className="h-6 w-16 rounded mt-1" />
      ) : (
        <p className="font-display text-2xl text-text leading-none mt-1 tabular-nums">
          {value.toLocaleString("en-US")}
          {unit}
        </p>
      )}
      <p className="text-[11.5px] text-text-faint mt-1">{label}</p>
    </div>
    </Reveal>
  );
}
